/**
 * Tagada Rx — CRM fee rules (pure).
 *
 * A transaction is Rx iff its processor's `options.tagadapayAccountId` is a
 * `tagadapay_accounts` row tagged `rx`. The I/O side (which processors are Rx)
 * lives in `src/app/services/rx/rxTransactions.ts::resolveRxProcessorTpas`;
 * this module only takes the resolved set and does the math.
 *
 * Business rules (docs/RX_PAYMENTS_DESIGN.md §1, §3.4, §3.5):
 *   - Every successful Rx transaction carries the "TagadaRx" CRM fee, a
 *     percentage of the whole Rx GMV (default 3.9% = 390 bps). It REPLACES
 *     the standard per-success CRM fees: an Rx transaction never enters the
 *     approved / subclub / subscription / upsell / retry / wallet categories.
 *   - The fee is collected at payment (Tagada is merchant of record and nets
 *     its fees before paying the brand out). On the weekly CRM invoice it is a
 *     dedicated, informational line with 0 due.
 *   - Partners: grid line `rxTransaction` (buy, default 190 bps). The merchant
 *     sell rate can't go below the Tagada public price (390 bps) unless the
 *     signed version sets `allowBelowPublic`, in which case the floor is the
 *     partner buy rate. No cap above.
 *
 * No DB, no clock, no I/O.
 */

/** Same primitive as `bpsFee` in ./crm-fees (inlined to avoid an import cycle). */
const bpsOf = (amountCents: number, bps: number): number => Math.round((amountCents * bps) / 10_000);

/** Tagada public price of the TagadaRx CRM fee, in basis points (3.90%). */
export const RX_PUBLIC_SELL_BPS = 390;

/** Default partner buy rate on the TagadaRx line, in basis points (1.90%). */
export const RX_DEFAULT_BUY_BPS = 190;

/** Signed-grid line for Rx transactions (bps on successful Rx volume). */
export interface RxTransactionLine {
    enabled: boolean;
    bps: number;
    /**
     * `rxAllowBelowPublic`: when true, the merchant sell rate may go below the
     * Tagada public price, down to this line's buy rate. Default false.
     * Part of the signed grid (content-hashed), so only a signed version can
     * open it.
     */
    allowBelowPublic?: boolean;
}

/** Default Rx line offered when an operator adds Rx to a partner grid. */
export const DEFAULT_RX_TRANSACTION_LINE: RxTransactionLine = {
    enabled: true,
    bps: RX_DEFAULT_BUY_BPS,
    allowBelowPublic: false,
};

/** True when a signed grid line opens Tagada Rx for the partner. */
export function gridHasRx(line: RxTransactionLine | null | undefined): boolean {
    return !!line?.enabled;
}

/**
 * Lowest merchant sell rate (bps) allowed on Rx transactions.
 *
 *   - no partner line (direct merchant, or partner without Rx) → public price
 *   - partner line, `allowBelowPublic` false → max(public price, buy)
 *   - partner line, `allowBelowPublic` true  → buy
 */
export function rxSellFloorBps(line: RxTransactionLine | null | undefined): number {
    if (!line?.enabled) return RX_PUBLIC_SELL_BPS;
    const buy = Math.max(0, line.bps ?? 0);
    return line.allowBelowPublic ? buy : Math.max(RX_PUBLIC_SELL_BPS, buy);
}

/**
 * Effective merchant sell rate on Rx for a partner grid + markup:
 * `max(buy + markup, floor)`. A partner without an Rx line keeps the public
 * price (its merchants' Rx fee is never free).
 */
export function resolveRxSellBps(
    line: RxTransactionLine | null | undefined,
    markupBps: number | null | undefined,
): number {
    if (!line?.enabled) return RX_PUBLIC_SELL_BPS;
    const sell = Math.max(0, line.bps ?? 0) + Math.max(0, markupBps ?? 0);
    return Math.max(sell, rxSellFloorBps(line));
}

/**
 * Validate a requested merchant Rx sell rate. Returns an error message, or
 * null when the rate is allowed.
 */
export function checkRxSellBps(
    bps: number,
    line: RxTransactionLine | null | undefined,
): string | null {
    if (!Number.isInteger(bps) || bps < 0 || bps > 10_000) {
        return 'The TagadaRx rate must be between 0% and 100%.';
    }
    const floor = rxSellFloorBps(line);
    if (bps < floor) {
        return `The TagadaRx rate can't go below ${(floor / 100).toFixed(2)}%.`;
    }
    return null;
}

/** True when the transaction ran on a processor attached to an Rx TPA. */
export function isRxTransaction(
    processorId: string | null | undefined,
    rxProcessorIds: { has(id: string): boolean },
): boolean {
    return !!processorId && rxProcessorIds.has(processorId);
}

/** Partition transactions into standard and Rx, preserving order. */
export function splitRxTransactions<T extends { processorId?: string | null }>(
    txns: readonly T[],
    rxProcessorIds: { has(id: string): boolean },
): { standard: T[]; rx: T[] } {
    const standard: T[] = [];
    const rx: T[] = [];
    for (const t of txns) {
        (isRxTransaction(t.processorId, rxProcessorIds) ? rx : standard).push(t);
    }
    return { standard, rx };
}

/**
 * `billing_reports.fees_breakdown.rxTransactions` — the dedicated,
 * already-collected invoice line. `total` is ALWAYS 0 (nothing due on the
 * invoice); the fee actually taken at payment is `collectedTotal`.
 */
export interface RxFeeEntry {
    enabled: boolean;
    count: number;
    /** Rx GMV in cents (USD at compute time). */
    volume: number;
    volumeCurrencyBreakdown: Record<string, number>;
    unitPrice: 0;
    percentageRate: number;
    fixedTotal: 0;
    /** Fee on the Rx volume (= collectedTotal). */
    percentageTotal: number;
    /** Amount due on the invoice: always 0. */
    total: 0;
    collectedAtPayment: true;
    /** TagadaRx fee taken at payment, in cents. */
    collectedTotal: number;
}

export function buildRxFeeEntry(input: {
    count: number;
    volume: number;
    volumeCurrencyBreakdown?: Record<string, number>;
    bps: number;
}): RxFeeEntry {
    const fee = bpsOf(input.volume, input.bps);
    return {
        enabled: true,
        count: input.count,
        volume: input.volume,
        volumeCurrencyBreakdown: input.volumeCurrencyBreakdown ?? {},
        unitPrice: 0,
        percentageRate: input.bps,
        fixedTotal: 0,
        percentageTotal: fee,
        total: 0,
        collectedAtPayment: true,
        collectedTotal: fee,
    };
}

/** Plan rate for Rx, falling back to the public price for pre-column rows. */
export function planRxBps(plan: { pctPerRxTransaction?: number | null } | null | undefined): number {
    const v = plan?.pctPerRxTransaction;
    return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : RX_PUBLIC_SELL_BPS;
}
